import { WorkerRequirementStatus } from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import {
  type CheckrApplicationStatus,
  CheckrReportStatus,
} from "@src/appV2/Accounts/Documents/types";

interface GetCheckrRequirementStatusProps {
  status: WorkerRequirementStatus;
  checkrApplicationStatus?: CheckrApplicationStatus;
  isRunBackgroundCheckOnBookingEnabled: boolean;
  isValid: boolean;
}

/**
 * Determines the effective status for Checkr background checks
 *
 * **Why this logic exists:**
 * When the feature flag is ON, background checks are automatically submitted/renewed for workers.
 * Workers don't need to worry about renewals happening in the background - we show APPROVED
 * to provide peace of mind when they have a valid document.
 *
 * **Scenarios:**
 * - Worker has approved doc expiring soon → Show APPROVED (auto-renewal in progress)
 * - Worker has approved doc + new submission in review → Show APPROVED (don't confuse them)
 * - We submitted to Checkr, they have valid doc → Show APPROVED (no need to bother them)
 *
 * @example Feature flag ON + Worker has valid document (isValid: true)
 * - EXPIRING → APPROVED (auto-renewal, don't worry user)
 * - IN_REVIEW → APPROVED (renewal pending, but current doc valid)
 * - Checkr PENDING → APPROVED (submitted to Checkr, current doc valid)
 *
 * @example Feature flag ON + Worker needs document (isValid: false)
 * - Checkr PENDING → IN_REVIEW (show actual submission status)
 * - Other statuses → unchanged
 *
 * @example Feature flag OFF
 * - Checkr PENDING → IN_REVIEW
 * - Other statuses → unchanged
 *
 * @param props.status - Current requirement status
 * @param props.checkrApplicationStatus - Checkr webhook status
 * @param props.isRunBackgroundCheckOnBookingEnabled - Feature flag for auto-submission
 * @param props.isValid - Worker has valid background check document
 */
export function getCheckrRequirementStatus(props: GetCheckrRequirementStatusProps) {
  const { status, checkrApplicationStatus, isRunBackgroundCheckOnBookingEnabled, isValid } = props;

  if (
    isRunBackgroundCheckOnBookingEnabled &&
    isValid &&
    (status === WorkerRequirementStatus.EXPIRING ||
      status === WorkerRequirementStatus.IN_REVIEW ||
      (isDefined(checkrApplicationStatus) &&
        checkrApplicationStatus.reportStatus === CheckrReportStatus.PENDING))
  ) {
    return WorkerRequirementStatus.APPROVED;
  }

  if (
    isDefined(checkrApplicationStatus) &&
    checkrApplicationStatus.reportStatus === CheckrReportStatus.PENDING
  ) {
    return WorkerRequirementStatus.IN_REVIEW;
  }

  return status;
}
